import React, { useCallback, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { DropDownItemOptionComponentProps, SelectDropdown, SelectOption } from '../../../../../../../../components/SelectDropdown/SelectDropdown'
import { $createHeadingNode, $createQuoteNode, HeadingTagType } from '@lexical/rich-text'
import { Button } from '../../../../../../../../components/Button/Button'
import { $createParagraphNode, $getSelection, $isRangeSelection, LexicalEditor } from 'lexical'
import { $setBlocksType } from '@lexical/selection'
import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND } from '@lexical/list'
import { BlockType, RichTextFormatting } from './hooks'

const RICH_TEXT_OPTIONS: { [id in BlockType]: { label: string; icon: string } } = {
  h1: {
    label: 'Very Large Heading',
    icon: 'bi-type-h1',
  },
  h2: {
    label: 'Large Heading',
    icon: 'bi-type-h2',
  },
  h3: {
    label: 'Medium Heading',
    icon: 'bi-type-h3',
  },
  h4: {
    label: 'Small Heading',
    icon: 'bi-type-h4',
  },
  h5: {
    label: 'Very Small Heading',
    icon: 'bi-type-h5',
  },
  paragraph: {
    label: 'Normal',
    icon: 'bi-type',
  },
  bullet: {
    label: 'Bullet List',
    icon: 'bi-list-ul',
  },
  number: {
    label: 'Numbered List',
    icon: 'bi-list-ol',
  },
  quote: {
    label: 'Quote',
    icon: 'bi-quote',
  },
}

export interface RichTextDropdownProps {
  editor: LexicalEditor
  formatting: RichTextFormatting
  disabled?: boolean
}

export const RichTextDropdown: React.FC<RichTextDropdownProps> = ({ editor, formatting, disabled = false }) => {
  const { selectedBlockType } = formatting

  const options = useMemo(() => {
    return Object.entries(RICH_TEXT_OPTIONS).map(([id, { label }]) => {
      return {
        id,
        label,
      }
    })
  }, [])

  const [selectedOption, setSelectedOption] = useState<SelectOption>(options[0])

  useEffect(() => {
    const selectOptionForBlockType = options.find((option) => option.id === selectedBlockType)
    if (selectOptionForBlockType) {
      setSelectedOption(selectOptionForBlockType)
    }
  }, [selectedBlockType, options])

  const formatParagraph = () => {
    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode())
      }
    })
  }

  const formatHeading = (headingSize: HeadingTagType) => {
    if (selectedBlockType !== headingSize) {
      editor.update(() => {
        const selection = $getSelection()
        $setBlocksType(selection, () => $createHeadingNode(headingSize))
      })
    }
  }

  const formatBulletList = () => {
    if (selectedBlockType !== 'bullet') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
    } else {
      formatParagraph()
    }
  }

  const formatNumberedList = () => {
    if (selectedBlockType !== 'number') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
    } else {
      formatParagraph()
    }
  }

  const formatQuote = () => {
    if (selectedBlockType !== 'quote') {
      editor.update(() => {
        const selection = $getSelection()
        $setBlocksType(selection, () => $createQuoteNode())
      })
    }
  }

  const handleOptionSelected = (option: SelectOption) => {
    const { id: optionId } = option
    setSelectedOption(option)
    switch (optionId as BlockType) {
      case 'h1':
        formatHeading('h1')
        return
      case 'h2':
        formatHeading('h2')
        return
      case 'h3':
        formatHeading('h3')
        return
      case 'h4':
        formatHeading('h4')
        return
      case 'h5':
        formatHeading('h5')
        return
      case 'paragraph':
        formatParagraph()
        return
      case 'bullet':
        formatBulletList()
        return
      case 'number':
        formatNumberedList()
        return
      case 'quote':
        formatQuote()
        return
    }
  }

  const RichTextDropdownDropdownItemOptionComponent: React.FC<DropDownItemOptionComponentProps> = useCallback(
    ({ optionId, optionLabel, onClick, selected }) => {
      const icon = RICH_TEXT_OPTIONS[optionId as BlockType]?.icon

      return (
        <Button variant='borderless' className={classNames('p-1 gap-1 align-items-center', { 'zocks-bg-primary-light': selected })} onClick={onClick}>
          <i className={`bi ${icon}`} />
          <div className='paragraph-small'>{optionLabel}</div>
        </Button>
      )
    },
    [],
  )

  const icon = useMemo(() => {
    const { id: selectedOptionId } = selectedOption
    return RICH_TEXT_OPTIONS[selectedOptionId as BlockType]?.icon
  }, [selectedOption])

  return (
    <SelectDropdown
      className='py-1 px-2 flex-grow-1'
      options={options}
      initialSelectedOption={selectedOption}
      onOptionSelected={handleOptionSelected}
      icon={icon}
      iconClassName='rich-text-toolbar-icon fs-5'
      DropDownItemOptionComponent={RichTextDropdownDropdownItemOptionComponent}
      disabled={disabled}
    />
  )
}
